import dayjs from "dayjs";
import { useAuth } from "@/contexts/AuthContext";
import { useGetOrderTimeSlots } from "@/services/Orders";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EventCard from "@/components/atoms/EventCard";
import ScheduleHeader from "@/components/atoms/ScheduleHeader";
import MonthView from "@/components/atoms/MonthView";
import WeekView from "@/components/atoms/WeekView";
import styles from "./styles.module.css";
import "@mantine/schedule/styles.css";
import { Stack } from "@mantine/core";
import { DateStringValue, getStartOfWeek, ScheduleViewLevel } from "@mantine/schedule";
import { OrderTimeSlot } from "@/types";

export const OrderSchedule = () => {
  const [events, setEvent] = useState<any[]>([]);
  const [date, setDate] = useState<DateStringValue>(dayjs().format("YYYY-MM-DD"));
  const [view, setView] = useState<ScheduleViewLevel>("week");

  const { user, currentCompany } = useAuth();
  const { i18n, t } = useTranslation();

  const nav = getNavigationHandlers(date, view);

  const labels = {
    today: dayjs(new Date()).format("DD.MM.YYYY"),
    month: t("common:month"),
    week: t("common:week"),
    day: t("common:day"),
    year: t("common:year"),
    allDay: t("common:allDay"),
  };

  const { data, isLoading } = useGetOrderTimeSlots(currentCompany?.companyId as number, undefined, undefined, {
    enabled: !!currentCompany?.companyId,
  });

  useEffect(() => {
    if (isLoading) return;
    if (!data) return;

    const toEvent = (slot: OrderTimeSlot, type: "incoming" | "outgoing") => {
      if (!slot || !slot.startDate) return null;

      const startDateObj = new Date(slot.startDate);
      const endDateObj = slot.endDate ? new Date(slot.endDate) : startDateObj;

      if (endDateObj < startDateObj) {
        return;
      }
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return null;

      return {
        id: slot.orderId,
        title: `${slot.counterPartyName} - ${slot.companySpecificproduct.companySpecificProductName} - ${slot?.salesItem?.nameTranslated?.[user?.language?.id as number] ?? slot.salesItem.salesItem ?? ""}`,
        start: slot.startDate,
        end: slot.endDate,
        color: slot.orderType.type === "BUY" ? "#039355" : "#DD3310",
        orderSlot: slot,
        direction: slot.orderType.type,
      };
    };

    const incomingEvents = (data.incomingOrders ?? []).map((s) => toEvent(s, "incoming")).filter(Boolean);

    const outgoingEvents = (data.outgoingOrders ?? []).map((s) => toEvent(s, "outgoing")).filter(Boolean);

    setEvent([...incomingEvents, ...outgoingEvents]);
  }, [data, isLoading]);

  function getHeaderLabel(date: DateStringValue, view: ScheduleViewLevel) {
    const currentLanguage = i18n.language;
    const locale = currentLanguage.startsWith("no") ? "nb" : "en";
    const d = dayjs(date).locale(locale);

    switch (view) {
      case "week": {
        const start = dayjs(getStartOfWeek({ date, firstDayOfWeek: 1 })).locale(locale);
        const end = start.add(6, "day");
        if (start.month() === end.month()) {
          return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
        }
        return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
      }
      case "month":
        return d.format("MMMM YYYY");
      default:
        return "";
    }
  }

  const commonViewProps = {
    events: events,
    date: date,
    onDateChange: setDate,
    labels: labels,
    renderEventBody: (event: any) => {
      const fullEvent = events.find((e) => e.id === event.id);
      return <EventCard event={fullEvent?.orderSlot || event.orderSlot || event} />;
    },
  };

  const renderView = () => {
    switch (view) {
      case "week":
        return <WeekView {...commonViewProps} />;
      case "month":
        return <MonthView {...commonViewProps} />;
      default:
        return <MonthView {...commonViewProps} />;
    }
  };

  return (
    <Stack>
      <ScheduleHeader className={styles.header}>
        <ScheduleHeader.Previous
          onClick={() => setDate(nav.previous.format("YYYY-MM-DD") as DateStringValue)}
          labels={labels}
          radius={0}
        />
        <ScheduleHeader.Control interactive={false} miw={200} radius={0}>
          {getHeaderLabel(date, view)}
        </ScheduleHeader.Control>
        <ScheduleHeader.Next
          onClick={() => setDate(nav.next.format("YYYY-MM-DD") as DateStringValue)}
          labels={labels}
          radius={0}
        />
        <ScheduleHeader.Today
          onClick={() => setDate(dayjs().format("YYYY-MM-DD") as DateStringValue)}
          labels={labels}
          lang={"no"}
          radius={0}
        />
        <div style={{ marginInlineStart: "auto" }}>
          <ScheduleHeader.ViewSelect
            views={["week", "month"]}
            value={view}
            onChange={setView}
            radius={0}
            labels={labels}
          />
        </div>
      </ScheduleHeader>
      {renderView()}
    </Stack>
  );
};

export default OrderSchedule;

function getNavigationHandlers(date: DateStringValue, view: ScheduleViewLevel) {
  const d = dayjs(date);
  switch (view) {
    case "week":
      return {
        previous: d.subtract(1, "week"),
        next: d.add(1, "week"),
      };
    case "month":
      return {
        previous: d.subtract(1, "month").startOf("month"),
        next: d.add(1, "month").startOf("month"),
      };
    default:
      return {
        previous: d,
        next: d,
      };
  }
}
